import type { PersistedDesktopWindow } from "../types";

const taskbarHeight = 48;
const desktopTop = 10;
const iconWidth = 82;
const iconColumnStep = 90;
const iconRowStep = 92;
const desktopRightInset = 10;

export function clampWindowToViewport<T extends PersistedDesktopWindow>(item: T, viewportWidth: number, viewportHeight: number): T {
  const availableWidth = Math.max(1, Math.floor(viewportWidth));
  const availableHeight = Math.max(1, Math.floor(viewportHeight) - taskbarHeight);
  const minimumWidth = Math.min(420, availableWidth);
  const minimumHeight = Math.min(300, availableHeight);
  const width = Math.max(minimumWidth, Math.min(item.width, availableWidth));
  const height = Math.max(minimumHeight, Math.min(item.height, availableHeight));
  const x = Math.max(0, Math.min(item.x, availableWidth - width));
  const y = Math.max(0, Math.min(item.y, availableHeight - height));
  return { ...item, x, y, width, height };
}

export function visibleIconCapacity(viewportWidth: number, viewportHeight: number): number {
  const rows = Math.max(1, Math.floor((viewportHeight - 70) / iconRowStep));
  const columns = Math.max(1, Math.floor((viewportWidth - desktopRightInset - desktopTop - iconWidth) / iconColumnStep) + 1);
  return rows * columns;
}

export function keepIconSlotsVisible(
  current: Record<string, number>,
  recordIds: string[],
  viewportWidth: number,
  viewportHeight: number
): Record<string, number> {
  const fixed: Record<string, number> = { "this-vault": 0, trash: 1, "password-manager": 2, authenticator: 3, todo: 4, calendar: 5, soundcloud: 6, filesharing: 7 };
  const capacity = visibleIconCapacity(viewportWidth, viewportHeight);
  const occupied = new Set(Object.values(fixed).filter((slot) => slot < capacity));
  const next: Record<string, number> = { ...fixed };
  const unplaced: string[] = [];

  for (const id of recordIds) {
    const slot = current[id];
    if (typeof slot === "number" && Number.isInteger(slot) && slot >= 8 && slot < capacity && !occupied.has(slot)) {
      next[id] = slot;
      occupied.add(slot);
    } else {
      unplaced.push(id);
    }
  }

  for (const id of unplaced) {
    let slot = 8;
    while (slot < capacity && occupied.has(slot)) slot++;
    if (slot >= capacity) {
      while (occupied.has(slot)) slot++;
    }
    next[id] = slot;
    occupied.add(slot);
  }

  return next;
}
